import { Link, createFileRoute } from '@tanstack/react-router';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatDateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { STATUS_TONE, modeLabel, statusLabel, useImportRuns } from '@/lib/qbo-history';

export const Route = createFileRoute('/settings/migration/history/')({ component: History });

/** Screen G13 (document 06): every QuickBooks transaction import of the client. */
function History() {
  const orgId = useOrgId();
  const runs = useImportRuns();
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('settingsPages.migration.history.title')}
        description={t('settingsPages.migration.history.description')}
        actions={
          <Button asChild>
            <Link to="/settings/migration/transactions">
              {t('settingsPages.migration.history.importTransactions')}
            </Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : runs.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : runs.isError ? (
        <EmptyState
          title={t('settingsPages.migration.history.couldNotLoad')}
          hint={runs.error.message}
        />
      ) : !runs.data.length ? (
        <EmptyState
          title={t('settingsPages.migration.history.empty')}
          hint={t('settingsPages.migration.history.emptyHint')}
        />
      ) : (
        <Table className="max-w-6xl" data-testid="qbo-import-history">
          <TableHeader>
            <TableRow>
              <TableHead>{t('settingsPages.migration.history.col.range')}</TableHead>
              <TableHead>{t('settingsPages.migration.history.col.import')}</TableHead>
              <TableHead>{t('settingsPages.migration.history.col.status')}</TableHead>
              <TableHead className="text-end">
                {t('settingsPages.migration.history.col.imported')}
              </TableHead>
              <TableHead className="text-end">
                {t('settingsPages.migration.history.col.skipped')}
              </TableHead>
              <TableHead className="text-end">
                {t('settingsPages.migration.history.col.notImported')}
              </TableHead>
              <TableHead>{t('settingsPages.migration.history.col.by')}</TableHead>
              <TableHead>{t('settingsPages.migration.history.col.finished')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="tnum">
                  {formatDate(r.from)} – {formatDate(r.to)}
                </TableCell>
                <TableCell>{modeLabel(r.mode)}</TableCell>
                <TableCell>
                  <StatusChip tone={STATUS_TONE[r.status]}>{statusLabel(r.status)}</StatusChip>
                </TableCell>
                <TableCell className="tnum text-end">{r.imported}</TableCell>
                <TableCell className="tnum text-end">{r.skipped}</TableCell>
                <TableCell className="tnum text-end">{r.failed}</TableCell>
                <TableCell>{r.startedByName ?? ''}</TableCell>
                <TableCell className="tnum">
                  {r.finishedAt ? formatDateTime(r.finishedAt) : ''}
                </TableCell>
                <TableCell className="text-end">
                  <Link
                    className="text-sm underline"
                    to="/settings/migration/transactions"
                    search={{ run: r.id }}
                  >
                    {t('settingsPages.migration.history.open')}
                  </Link>
                  {r.imported || r.skipped ? (
                    <Link
                      className="ms-3 text-sm underline"
                      to="/settings/migration/history/$runId"
                      params={{ runId: r.id }}
                    >
                      {t('settingsPages.migration.history.transactions')}
                    </Link>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
